import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from "../api/axiosInstance";
import API_ENDPOINTS from "../api/endpoints";
import { toast } from 'react-toastify';
import CardSelector from '../components/Card/CardSelector';
import { useNavigate } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';
import { IoChevronBackOutline } from "react-icons/io5";

function Showdown() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selecting, setSelecting] = useState(null);
    const [cardSelection, setCardSelection] = useState({});
    const [cardSelectionMap, setCardSelectionMap] = useState({});
    const [mocked, setMocked] = useState({});
    const [apiCards, setApiCards] = useState({
        usedCards: [],
        availableCards: [],
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`${API_ENDPOINTS.SHOWDOWN}?id=${id}`);
            setData(res.data);
            const updatedMocked = {};
            Object.entries(res.data.showdown).forEach(([pos, value]) => {
                if (value === null) updatedMocked[pos] = true;
            });
            setMocked(updatedMocked);
        } catch {
            toast.error("Failed to fetch showdown data");
        } finally {
            setLoading(false);
        }
    },[id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const fetchCards = async () => {
            try {
                const response = await axiosInstance.get(`${API_ENDPOINTS.AVILABELCARD}?id=${id}`);
                setApiCards({
                    usedCards: response.data.usedCards || [],
                    availableCards: response.data.availableCards || [],
                });
            } catch (error) {
                setApiCards({ usedCards: [], availableCards: [] });
            }
        };
        if (id) fetchCards();
    }, [id]);

    const handleSelectCard = (pos) => {
        setSelecting(pos);
        setCardSelection({});
    };

    const handleMock = (pos) => {
        setMocked(prev => ({ ...prev, [pos]: !prev[pos] }));
        setCardSelectionMap(prev => ({ ...prev, [pos]: prev[pos] ? undefined : null }));
    };

    const saveSelectedCards = () => {
        const { value1, type1, value2, type2 } = cardSelection;
        if (!value1 || !type1 || !value2 || !type2) {
            toast.error("Please select two cards");
            return;
        }
        if (value1 === value2 && type1 === type2) {
            toast.error("Cards must be different");
            return;
        }

        const holding = `${value1}${type1}${value2}${type2}`;
        setCardSelectionMap(prev => ({ ...prev, [selecting]: holding }));
        setMocked(prev => ({ ...prev, [selecting]: false }));
        setSelecting(null);
    };

    const allPositionsHandled = () => {
        if (!data) return false;
        return Object.entries(data.showdown).every(([pos, value]) => {
            return (
                value ||
                mocked[pos] === true ||
                (cardSelectionMap[pos] && cardSelectionMap[pos].length === 4)
            );
        });
    };

    const submitAll = async () => {
        const updatedShowdown = {};

        Object.entries(data.showdown).forEach(([pos, value]) => {
            if (value) {
                // Already saved → keep as is
                updatedShowdown[pos] = value;
            } else if (mocked[pos]) {
                // Mucked by user
                updatedShowdown[pos] = null;
            } else if (cardSelectionMap[pos]) {
                // New selection
                updatedShowdown[pos] = cardSelectionMap[pos];
            } else {
                // Not set at all
                updatedShowdown[pos] = "";
            }
        });


        const payload = {
            board: data.board,
            showdown: updatedShowdown,
        };

        setLoading(true);
        try {
            const res = await axiosInstance.patch(`${API_ENDPOINTS.SHOWDOWN}?id=${id}`, payload);
            toast.success("Hand Details Saved");
            setMocked({});
            setCardSelectionMap({});
            fetchData();
            navigate(`/Sessions/SessionCard/${res.data.sessionId}`);
        } catch {
            toast.error("Failed to update showdown");
        } finally {
            setLoading(false);
        }
    };


    const getCardArray = (value) => {
        if (!value || value.length !== 4) return [
            { label: '', value: '' },
            { label: '', value: '' }
        ];
        return [
            { label: value.slice(0, 2), value: value.slice(0, 2) },
            { label: value.slice(2, 4), value: value.slice(2, 4) }
        ];
    };

    const getBoardCards = (board) => {
        if (!board) return [];
        return board.match(/.{1,2}/g) || [];
    };

    const renderPositionRow = (pos, value) => {
        const cards = getCardArray(
            value ||
            (cardSelectionMap[pos] === null ? '' : cardSelectionMap[pos])
        );
        const isPredefined = pos === data.position;

        return (
            <div key={pos} className="flex items-center mb-2">
                <div className={` font-bold text-lg w-20 tracking-wide ${pos === data.position ? 'text-white' : 'text-[#1976d2]'}`}>{pos}</div>
                <div
                    className={`flex gap-2 ${isPredefined || mocked[pos] ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={() => {
                        if (!isPredefined && !mocked[pos]) handleSelectCard(pos);
                    }}
                >
                    {cards.map((card, idx) => (
                        <div
                            key={idx}
                            className={`w-12 h-16 bg-white rounded-lg text-black flex items-center flex-col justify-center text-lg font-bold ${mocked[pos] ? 'opacity-35' : ''}`}
                        >
                            <span className="pt-1">{card.label.slice(0, 1)}</span>
                            <span className={`card-img ${card.value.slice(1, 2)}`}></span>
                        </div>
                    ))}
                </div>
                <div className="ml-4">
                    {!isPredefined && (
                        <button
                            className={`border border-[#1976d230] px-4 py-1 rounded transition-all 
                                ${mocked[pos] ? 'bg-[#1976d230] text-white' : 'bg-[#1976d2] text-white hover:bg-[#145ea8]'}`}
                            onClick={() => handleMock(pos)}
                        >
                            {mocked[pos] ? 'Unmuck' : 'Muck'}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    if (loading) {
        return <div className="flex justify-center items-center min-h-[200px]">
            <ThreeDots
                visible={true}
                height="80"
                width="80"
                color="#1976d2"
                radius="9"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClass="dots-loading additional-class"
            />
        </div>;
    }

    if (!data) return <div className="text-white">No data</div>;

    return (
        <div className='p-6'>
            <div className="flex items-center mb-6">
                <button
                    className="border border-border hover:bg-gray-800 text-white text-xl mr-3 w-10 h-10 flex items-center justify-center rounded-lg"
                    onClick={() => navigate(-1)}
                    type="button"
                >
                    <IoChevronBackOutline />
                </button>
                <h1 className="font-bold text-xl text-white m-0">Showdown</h1>
            </div>
            <div className="text-white w-full p-6 rounded-sm bg-[#111]">
                {/* Board */}
                {getBoardCards(data.board).length > 0 && (
                    <div className="flex items-center mb-8 gap-2">
                        {getBoardCards(data.board).map((card, idx) => (
                            <div
                                key={idx}
                                className="w-12 h-16 bg-white rounded-lg text-black flex items-center flex-col justify-center text-lg font-bold"
                            >
                                <span className="pt-1">{card[0]}</span>
                                <span className={`card-img ${card[1]}`}></span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Positions */}
                <div className="flex flex-col gap-2">
                    {Object.entries(data.showdown).map(([pos, value]) =>
                        renderPositionRow(pos, value)
                    )}
                </div>

                {/* Save All */}

                <div className="flex justify-end mt-5">
                    <button
                        className={`px-6 py-2 rounded font-bold ${allPositionsHandled()
                            ? 'bg-[#1976d2] text-white hover:bg-[#145ea8]'
                            : 'bg-gray-600 text-gray-300 cursor-not-allowed'
                            }`}
                        disabled={!allPositionsHandled()}
                        onClick={submitAll}
                    >
                        Finish
                    </button>
                </div>

                {/* Modal */}
                {selecting && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[99999]">
                        <div className="bg-gray-800 p-8 rounded-xl shadow-2xl min-w-[340px] flex flex-col items-center">
                            <h2 className="text-[#1976d2] text-2xl font-bold mb-4">
                                Select Cards for {selecting}
                            </h2>
                            <CardSelector
                                count={2}
                                value={cardSelection}
                                onChange={setCardSelection}
                                screen={true}
                                usedCards={[
                                    ...apiCards.usedCards,
                                    ...Object.values(cardSelectionMap)
                                        .filter(Boolean)
                                        .flatMap(val => [val.slice(0, 2), val.slice(2, 4)]),
                                    ...(cardSelection.value1 && cardSelection.type1
                                        ? [`${cardSelection.value1}${cardSelection.type1}`]
                                        : []),
                                    ...(cardSelection.value2 && cardSelection.type2
                                        ? [`${cardSelection.value2}${cardSelection.type2}`]
                                        : [])
                                ]}
                            />
                            <div className="flex gap-6 mt-6">
                                <button
                                    onClick={saveSelectedCards}
                                    className="bg-[#1976d2] hover:bg-[#145ea8] px-6 py-2 rounded text-white font-bold"
                                >
                                    Save
                                </button>
                                <button
                                    onClick={() => setSelecting(null)}
                                    className="border-2 border-[#1976d230] text-white font-bold px-6 py-2 rounded-lg hover:bg-[#1976d2] transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Showdown;
